import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { MessageSquare, Plus, Send, Users, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamChat() {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [createDialog, setCreateDialog] = useState(false);
  const [chatName, setChatName] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchChats(); }, []);

  const fetchChats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/team-chats`, { withCredentials: true });
      setChats(data);
    } catch (err) { console.error(err); }
  };

  const fetchMessages = async (chatId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/team-chats/${chatId}/messages`, { withCredentials: true });
      setMessages(data.reverse());
    } catch (err) { console.error(err); }
  };

  const openChat = (chat) => {
    setActiveChat(chat);
    fetchMessages(chat.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChat) return;
    try {
      await axios.post(`${API_URL}/api/team-chats/${activeChat.id}/messages`, { content: newMessage }, { withCredentials: true });
      setNewMessage('');
      fetchMessages(activeChat.id);
    } catch (err) { toast.error('Failed to send'); }
  };

  const fetchOrgUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org`, { withCredentials: true });
      setOrgUsers(data.filter(u => u.id !== user?.id));
    } catch (err) { console.error(err); }
  };

  const createChat = async () => {
    if (!chatName.trim()) return toast.error('Chat name required');
    try {
      await axios.post(`${API_URL}/api/team-chats`, { name: chatName, members: selectedMembers }, { withCredentials: true });
      toast.success('Team chat created');
      setCreateDialog(false);
      setChatName('');
      setSelectedMembers([]);
      fetchChats();
    } catch (err) { toast.error('Failed'); }
  };

  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  if (activeChat) {
    return (
      <DashboardLayout>
        <div className="flex flex-col h-[calc(100vh-120px)]" data-testid="team-chat-messages">
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <Button variant="ghost" size="icon" onClick={() => setActiveChat(null)} className="text-muted-foreground"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h2 className="text-lg font-heading text-foreground">{activeChat.name}</h2>
              <p className="text-xs text-muted-foreground">{activeChat.members?.length} members</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-3">
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${m.sender_id === user?.id ? 'bg-[#00A6A0] text-[#0F1419]' : 'bg-muted text-foreground'}`}>
                  {m.sender_id !== user?.id && <p className="text-xs font-medium opacity-70 mb-0.5">{m.sender_name}</p>}
                  <p className="text-sm">{m.content}</p>
                  <p className="text-[10px] opacity-50 mt-1">{new Date(m.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-3 border-t border-border">
            <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a message..."
              className="bg-muted border-border text-foreground flex-1" data-testid="team-chat-input"
              onKeyDown={e => e.key === 'Enter' && sendMessage()} />
            <Button className="btn-primary" onClick={sendMessage} data-testid="send-team-message-btn"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="team-chat-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <Users className="w-7 h-7 inline mr-3 text-[#00A6A0]" />Team Chat
            </h1>
            <p className="text-muted-foreground mt-1">Group messaging for care teams</p>
          </div>
          {isAdmin && <Button className="btn-primary" onClick={() => { setCreateDialog(true); fetchOrgUsers(); }} data-testid="create-team-chat-btn"><Plus className="w-4 h-4 mr-2" />New Chat</Button>}
        </div>

        {chats.length === 0 ? (
          <div className="text-center py-16"><Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No team chats yet</p></div>
        ) : (
          <div className="space-y-2">
            {chats.map(chat => (
              <Card key={chat.id} className="glass-card border-border card-hover cursor-pointer" onClick={() => openChat(chat)} data-testid={`team-chat-${chat.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00A6A0]/10 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-[#00A6A0]" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground font-medium">{chat.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{chat.last_message || 'No messages yet'}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{chat.members?.length} members</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">Create Team Chat</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="text-xs text-muted-foreground">Chat Name</label>
                <Input value={chatName} onChange={e => setChatName(e.target.value)} placeholder="e.g., Morning Team" className="bg-muted border-border text-foreground" data-testid="team-chat-name-input" /></div>
              <div><label className="text-xs text-muted-foreground">Add Members</label>
                <div className="max-h-48 overflow-y-auto space-y-1 mt-1">
                  {orgUsers.map(u => (
                    <label key={u.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${selectedMembers.includes(u.id) ? 'bg-[#00A6A0]/10 text-[#00A6A0]' : 'text-muted-foreground hover:bg-muted/50'}`}>
                      <input type="checkbox" checked={selectedMembers.includes(u.id)} onChange={() => toggleMember(u.id)} className="rounded" />
                      {u.name} <span className="text-[10px] text-muted-foreground">({u.role})</span>
                    </label>
                  ))}
                </div>
              </div>
              <Button className="w-full btn-primary" onClick={createChat} data-testid="create-chat-submit-btn">Create Chat</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
